import type { OutreachRoundResultsResponse, OutreachRoundResultsStatus } from '@aegislab/shared';
import * as s from './OutreachRoundResults.css';

export interface OutreachRoundResultsProps {
  /** 부르는 중이거나 못 불렀을 때 빈 성과로 읽히지 않게 가른다. */
  state: 'loading' | 'error' | 'ready';
  results?: OutreachRoundResultsResponse;
}

const STATUS_BADGE: Record<OutreachRoundResultsStatus, string> = {
  TEST: '테스트 — 집계 안 함',
  NOT_SENT: '발송 안 됨',
  SEND_UNCONFIRMED: '발송 확인 안 됨',
  COLLECTING: '집계 중',
  FINAL: '7일 최종',
};

/** 수치 없이 상태만 적는 회차. */
const NO_FIGURES: Partial<Record<OutreachRoundResultsStatus, string>> = {
  TEST: '테스트 회차라 성과를 집계하지 않습니다.',
  NOT_SENT: '메일이 나가지 않은 회차라 성과가 없습니다.',
  SEND_UNCONFIRMED: '발송이 확인되면 집계를 시작합니다.',
};

const countFormat = new Intl.NumberFormat('ko-KR');
const rateFormat = new Intl.NumberFormat('ko-KR', {
  style: 'percent',
  maximumFractionDigits: 1,
});

/** 서버가 주지 않은 수치는 「—」 다 — 0 으로 채우지 않는다. */
const countText = (value: number | null) => (value === null ? '—' : countFormat.format(value));
const rateText = (value: number | null) => (value === null ? '—' : rateFormat.format(value));

function moment(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${date.getMonth() + 1}월 ${date.getDate()}일 ${hh}:${mm}`;
}

interface Figure {
  label: string;
  count: string;
  /** 발송 성공 대비 비율. 수치가 없거나 해당 없음이면 싣지 않는다. */
  rate?: string;
  basis?: string;
}

function figuresOf({ stibee, rates, form }: OutreachRoundResultsResponse): Figure[] {
  const productMissing = stibee !== null && stibee.clickedProduct === null;
  const withRate = (value: number | null, rate: number | null) =>
    value === null ? {} : { rate: rateText(rate) };
  return [
    {
      label: '발송 성공',
      count: countText(stibee?.sent ?? null),
      basis: '주소 기준 · 비율의 분모',
    },
    {
      label: '오픈 기록',
      count: countText(stibee?.opened ?? null),
      ...withRate(stibee?.opened ?? null, rates.opened),
      basis: '자동 오픈 포함',
    },
    {
      label: '설문 버튼 클릭',
      count: countText(stibee?.clickedSurvey ?? null),
      ...withRate(stibee?.clickedSurvey ?? null, rates.clickedSurvey),
    },
    productMissing
      ? { label: '제품 링크 클릭', count: '해당 없음' }
      : {
          label: '제품 링크 클릭',
          count: countText(stibee?.clickedProduct ?? null),
          ...withRate(stibee?.clickedProduct ?? null, rates.clickedProduct),
        },
    {
      label: '폼 제출',
      count: countText(form.submitted),
      ...withRate(form.submitted, rates.submitted),
      basis: '계정 기준',
    },
  ];
}

/** 0 과 다른 상태와 보조 수치를 문장으로 적는다. 순서는 스티비 → 폼 → 세는 방식이다. */
function notesOf(results: OutreachRoundResultsResponse): string[] {
  const { collection, stibee, stibeeWithin24h, form } = results;
  const notes: string[] = [];
  if (results.status === 'FINAL' && collection?.collectedAt) {
    notes.push(`${moment(collection.collectedAt)} 기준 값입니다. 발송 뒤 7일 안의 기록만 셉니다.`);
  }
  if (results.status === 'COLLECTING') {
    if (stibee === null) {
      if (!collection?.delayed) notes.push('첫 집계는 발송 1시간 뒤에 나옵니다.');
    } else if (collection?.collectedAt) {
      notes.push(
        `${moment(collection.collectedAt)} 기준 값이고, 발송 뒤 7일이 지나면 최종으로 굳습니다.`
      );
    }
  }
  if (collection?.delayed) notes.push('예정한 수집이 늦어지고 있습니다.');
  if (collection?.failed) {
    notes.push(
      stibee === null
        ? '마지막 수집이 실패했습니다. 다음 시각에 다시 읽습니다.'
        : results.status === 'FINAL'
          ? '7일 값을 끝내 읽지 못해 그 전에 읽은 값으로 닫았습니다.'
          : '마지막 수집이 실패해 그 전에 읽은 값을 보이고 있습니다.'
    );
  }

  const extras = [
    stibee && stibee.failed > 0 ? `발송 실패 주소 ${countFormat.format(stibee.failed)}` : null,
    stibee?.notSent ? `발송 안 된 주소 ${countFormat.format(stibee.notSent)}` : null,
    stibee && stibee.clickedUnclassified > 0
      ? `분류 안 된 클릭 ${countFormat.format(stibee.clickedUnclassified)}`
      : null,
    stibee && stibee.unsubscribed > 0
      ? `수신거부 ${countFormat.format(stibee.unsubscribed)}`
      : null,
  ].filter((part) => part !== null);
  if (extras.length > 0) notes.push(extras.join(' · '));
  if (stibeeWithin24h) {
    notes.push(
      `발송 뒤 24시간 안의 값은 오픈 기록 ${countFormat.format(stibeeWithin24h.opened)}, 설문 버튼 클릭 ${countFormat.format(stibeeWithin24h.clickedSurvey)}입니다.`
    );
  }

  if (form.roundCodeSent === false) {
    notes.push('이 회차 메일에는 회차 코드가 실리지 않아 폼 제출을 셀 수 없습니다.');
  }
  if (form.unreadable) {
    notes.push(
      form.lastReadAt
        ? `폼 응답을 읽지 못하고 있습니다. 마지막으로 읽은 때는 ${moment(form.lastReadAt)}입니다.`
        : '폼 응답을 읽지 못하고 있습니다.'
    );
  } else if (form.submitted === null) {
    notes.push('폼 응답을 아직 한 번도 읽지 않았습니다.');
  }
  if (form.recipientConfirmed !== null && form.recipientUnconfirmed !== null) {
    notes.push(
      [
        `받는 사람 확인 ${countFormat.format(form.recipientConfirmed)}`,
        `받는 사람 확인 안 됨 ${countFormat.format(form.recipientUnconfirmed)}`,
        form.needsCheck ? `확인 필요 ${countFormat.format(form.needsCheck)}` : null,
      ]
        .filter((part) => part !== null)
        .join(' · ')
    );
  }
  if (form.campaignUnattributed) {
    notes.push(
      `이 캠페인에는 회차 코드가 없는 응답이 ${countFormat.format(form.campaignUnattributed)}건 있습니다.`
    );
  }
  if (form.intakeEnded) notes.push('섭외 지표 퀘스트가 끝나 폼 응답을 더 받지 않습니다.');

  if (!results.recipientLinked) notes.push('받는 사람 기록이 없는 회차라 주소 단위로만 셉니다.');
  if (results.sharedAccounts > 0) {
    notes.push(
      `주소를 같이 쓰는 계정이 ${countFormat.format(results.sharedAccounts)}명이라 폼 비율이 실제보다 커질 수 있습니다.`
    );
  }
  return notes;
}

/**
 * 회차 성과 — 섭외 IA §3-6, 퀘스트 e91872f1 CP3.
 *
 * 발송 성공 → 오픈 기록 → 설문 버튼·제품 링크 클릭 → 폼 제출을 인원과 발송 성공 대비 비율로 보인다.
 * **분모는 발송 성공 주소 하나다** — 클릭 대비 폼처럼 앞 단계 대비 비율은 같은 사람을 잇지 못해 내지 않는다.
 * 「0」은 서버가 최종 수집을 마치고 사건이 없다고 할 때만 나오고, 나머지 비어 있는 자리는 「—」 와 문장이 든다.
 */
export function OutreachRoundResults({ state, results }: OutreachRoundResultsProps) {
  if (state === 'loading') return <p className={s.note}>회차 성과를 불러오는 중입니다.</p>;
  if (state === 'error' || !results) {
    return <p className={s.note}>회차 성과를 불러오지 못했습니다.</p>;
  }
  const headingId = `outreach-round-results-${results.roundId}`;
  const noFigures = NO_FIGURES[results.status];
  const notes = noFigures ? [] : notesOf(results);
  return (
    <section className={s.panel} aria-labelledby={headingId}>
      <div className={s.head}>
        <h4 id={headingId} className={s.heading}>
          회차 성과
        </h4>
        <span className={s.badge}>
          {/* 7일 값을 끝내 못 읽고 닫았으면 최종이긴 해도 7일 값이 아니다. */}
          {results.status === 'FINAL' && results.collection?.failed
            ? '최종 — 수집 실패'
            : STATUS_BADGE[results.status]}
        </span>
      </div>
      {noFigures ? (
        <p className={s.note}>{noFigures}</p>
      ) : (
        <>
          <dl className={s.funnel}>
            {figuresOf(results).map((figure) => (
              <div key={figure.label} className={s.stat}>
                <dt className={s.label}>{figure.label}</dt>
                <dd className={s.value}>
                  <span className={s.count}>{figure.count}</span>
                  {figure.rate === undefined ? null : <span className={s.rate}>{figure.rate}</span>}
                </dd>
                {figure.basis === undefined ? null : <dd className={s.basis}>{figure.basis}</dd>}
              </div>
            ))}
          </dl>
          {notes.length === 0 ? null : (
            <ul className={s.notes}>
              {notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
